import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const { completeGoogleLogin } = useAuth();

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!token) { setInvalid("This link is missing its token."); setChecking(false); return; }
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (err) {
        setInvalid(formatApiError(err.response?.data?.detail) || "This invite link is invalid.");
      } finally { setChecking(false); }
    };
    run();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      completeGoogleLogin(data.user);
      toast.success("Password set — welcome!");
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not set password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm" data-testid="set-password-page">
        <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
          <ShieldCheck size={26} />
        </div>
        <h1 className="font-serif text-3xl mb-2">Set up your portal</h1>

        {checking ? (
          <p className="text-muted-foreground">Checking your invite…</p>
        ) : invalid ? (
          <div data-testid="set-password-invalid">
            <p className="text-red-400 mb-6">{invalid}</p>
            <Link to="/login" className="text-primary hover:underline">Go to sign in</Link>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground mb-8">
              Create a password for <span className="text-foreground">{invite?.email}</span> to access your client portal.
            </p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="password">New password</Label>
                <Input id="password" type="password" value={password} required minLength={8}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                  className="mt-2 bg-zinc-900/60 border-white/10" data-testid="set-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" type="password" value={confirm} required
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password"
                  className="mt-2 bg-zinc-900/60 border-white/10" data-testid="set-password-confirm" />
              </div>
              <Button type="submit" disabled={loading} data-testid="set-password-submit"
                className="w-full bg-primary text-primary-foreground hover:bg-[#DBC197]">
                {loading ? "Setting up…" : <>Set password & continue <ArrowRight size={18} className="ml-2" /></>}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
